import React, { useEffect, Component } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import { Restaurant } from '../types';
import { SafetyBadge } from './SafetyBadge';
import { MapPin, AlertTriangle, ChevronRight } from 'lucide-react';
interface MapViewProps {
  restaurants: Restaurant[];
  selectedId: string | null;
  onSelectRestaurant: (id: string) => void;
  onViewDetails: (id: string) => void;
}
// Component to handle map center updates when selection changes
function MapController({
  selectedId,
  restaurants
}: {
  selectedId: string | null;
  restaurants: Restaurant[];
}) {
  const map = useMap();
  // Fix gray tiles: force Leaflet to recalculate on mount and resize
  useEffect(() => {
    // Immediate + delayed invalidation to catch flex layout settling
    map.invalidateSize();
    const t1 = setTimeout(() => map.invalidateSize(), 100);
    const t2 = setTimeout(() => map.invalidateSize(), 500);
    // Watch for container resizes
    const container = map.getContainer();
    const observer = new ResizeObserver(() => {
      map.invalidateSize();
    });
    observer.observe(container);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      observer.disconnect();
    };
  }, [map]);
  useEffect(() => {
    if (selectedId) {
      const restaurant = restaurants.find(r => r.id === selectedId);
      if (restaurant) {
        map.setView([restaurant.lat, restaurant.lng], 15, {
          animate: true,
          duration: 0.5
        });
      }
    }
  }, [selectedId, map, restaurants]);
  return null;
}
export function MapView({
  restaurants,
  selectedId,
  onSelectRestaurant,
  onViewDetails
}: MapViewProps) {
  // Center of Springfield, IL
  const defaultCenter: [number, number] = [39.791, -89.648];
  const createCustomIcon = (grade: string, isSelected: boolean) => {
    return L.divIcon({
      className: 'bg-transparent border-none',
      html: `<div class="grade-marker grade-marker-${grade} ${isSelected ? 'selected' : ''} w-8 h-8 md:w-10 md:h-10">${grade}</div>`,
      iconSize: [40, 40],
      iconAnchor: [20, 20],
      popupAnchor: [0, -20]
    });
  };
  return <div className="w-full h-full relative z-0" data-id="element-70">
      <MapContainer center={defaultCenter} zoom={14} className="w-full h-full" zoomControl={false} data-id="element-71">
        <TileLayer attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors' url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png" data-id="element-72" />

        <MapController selectedId={selectedId} restaurants={restaurants} data-id="element-73" />

        {restaurants.map(restaurant => {
        const isSelected = restaurant.id === selectedId;
        const latestInspection = restaurant.inspections[0];
        const violationCount = latestInspection?.violations.length || 0;
        return <Marker key={restaurant.id} position={[restaurant.lat, restaurant.lng]} icon={createCustomIcon(restaurant.currentGrade, isSelected)} eventHandlers={{
          click: () => onSelectRestaurant(restaurant.id)
        }} zIndexOffset={isSelected ? 1000 : 0} data-id="element-74">
              <Popup className="custom-popup" data-id="element-75">
                <div className="bg-white p-4 flex flex-col gap-3" data-id="element-76">
                  <div className="flex justify-between items-start gap-4" data-id="element-77">
                    <div data-id="element-78">
                      <h3 className="font-bold text-slate-900 text-base leading-tight mb-1" data-id="element-79">
                        {restaurant.name}
                      </h3>
                      <p className="text-xs font-medium text-slate-500" data-id="element-80">
                        {restaurant.cuisine}
                      </p>
                    </div>
                    <SafetyBadge grade={restaurant.currentGrade} data-id="element-81" />
                  </div>

                  <div className="flex items-start gap-1.5 text-xs text-slate-600" data-id="element-82">
                    <MapPin className="w-3.5 h-3.5 mt-0.5 shrink-0 text-slate-400" data-id="element-83" />
                    <span className="line-clamp-1" data-id="element-84">{restaurant.address}</span>
                  </div>

                  <div className="pt-3 border-t border-slate-100 flex items-center justify-between text-xs" data-id="element-85">
                    {violationCount > 0 ? <div className="flex items-center gap-1 text-amber-700 font-medium" data-id="element-86">
                        <AlertTriangle className="w-3.5 h-3.5" data-id="element-87" />
                        <span data-id="element-88">
                          {violationCount} issue
                          {violationCount !== 1 ? 's' : ''}
                        </span>
                      </div> : <div className="flex items-center gap-1 text-green-700 font-medium" data-id="element-89">
                        <span data-id="element-90">Perfect score</span>
                      </div>}
                    <span className="text-slate-400" data-id="element-91">
                      {new Date(restaurant.lastInspectionDate).toLocaleDateString(undefined, {
                    month: 'short',
                    day: 'numeric'
                  })}
                    </span>
                  </div>

                  <button onClick={e => {
                e.stopPropagation();
                onViewDetails(restaurant.id);
              }} className="mt-1 w-full flex items-center justify-center gap-1 bg-teal-50 text-teal-700 hover:bg-teal-100 py-2 rounded-lg text-sm font-semibold transition-colors" data-id="element-92">
                    View Details
                    <ChevronRight className="w-4 h-4" data-id="element-93" />
                  </button>
                </div>
              </Popup>
            </Marker>;
      })}
      </MapContainer>
    </div>;
}